import * as B from '@blueprintjs/core';
import {bbox} from '@turf/turf';
import classnames from 'classnames';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import {Link} from 'react-router-dom';

import MapOverlayDialog from 'app/components/MapOverlayDialog/MapOverlayDialog';
import {usePushNotification} from 'app/components/Notification';
import {OrderTypeSwitcher} from 'app/components/OrderImageryModal/OrderTypeSwitcher';
import {getResolutionCategoryForScene} from 'app/components/OrderImagerySidebar/utils';
import {SceneDetails, ScenePreviewGuidance} from 'app/components/SceneDetails';
import {ScenePreviewControls, ScenePreviewStage} from 'app/components/ScenePreviewStepper';
import {
  cartFullMessage,
  cartItemOrderBboxString,
  formatItemPrice,
  useCart,
  useRemoveWithUndo,
} from 'app/modules/Cart';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiImageryContract} from 'app/modules/Remote/Project';
import {useCurrentImageryBudget} from 'app/providers/ImageryContractProvider';
import {useImageryPrices} from 'app/providers/ImageryPricesProvider';
import * as C from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import {formatCost} from 'app/utils/costUtils';
import featureFlags, {useUserInfo} from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import {UTMArea, isRectanglePolygon} from 'app/utils/geoJsonUtils';
import {CachedApiGetterOptions, StatusMaybe, useTimestampMs} from 'app/utils/hookUtils';
import {
  getLayerKeyForScene,
  getOrderTypeFromSourceId,
  getScenePreviewKind,
  getThumbnailOverrideScene,
  isBundleableSource,
  orderableScenePairKey,
} from 'app/utils/imageryUtils';
import {getOrgPrefix, paysAtCheckout} from 'app/utils/organizationUtils';
import * as routeUtils from 'app/utils/routeUtils';
import * as userUtils from 'app/utils/userUtils';

import {CartItemDraft} from './hooks';
import {
  ContractDetailsDescription,
  ORDERABLE_SCENE_ERRORS,
  formatAcres,
  getBillableAcres,
  getPackageOrderText,
  purchaseInfoStatus,
  validateOrderableScene,
} from './orderImageryUtils';
import cs from './styles.module.scss';

export interface Props {
  orderableScene: I.ImmutableOf<ApiOrderableScene>;
  feature: I.ImmutableOf<ApiFeature>;
  organization: I.ImmutableOf<ApiOrganization>;
  selectedProjectId: string;
  role: ApiOrganizationUser['role'];

  disabledSources?: string[];
  blockedSources?: string[];

  getCurrentImageryContract: (
    opts: CachedApiGetterOptions
  ) => StatusMaybe<ApiImageryContract | null>;

  onClose: () => void;
  /** Called when user switches order type tabs (for PNEO sources). Parent should swap orderableScene. */
  onOrderTypeChange?: (orderType: VariableOrderType) => void;

  isOpen?: boolean;

  subsetFeature: geojson.Feature<geojson.Polygon | geojson.MultiPolygon> | null;
  orderableScenes: I.List<I.ImmutableOf<ApiOrderableScene>> | undefined;

  /**
   * Scenes the preview can step through, in the order the sidebar lists them. Prev/Next asks the
   * parent to swap `orderableScene` for the neighbour, so the whole modal follows the preview.
   */
  previewScenes?: readonly I.ImmutableOf<ApiOrderableScene>[];
  onPreviewSceneChange?: (scene: I.ImmutableOf<ApiOrderableScene>) => void;

  /** Shown on the cart item; the modal only has the project id. */
  projectName?: string;
}

export type VariableOrderType = 'truecolor' | 'multispectral';

// Square, in px. `$previewColumnWidth` in styles.module.scss sizes the column around it.
const PREVIEW_STAGE_SIZE = 420;

/**
 * A status line that enters the DOM empty and fills a paint later. A live region inserted already
 * populated is the one screen readers skip, and this line's subtree only mounts once the contract
 * loads — with a full-cart message sometimes already standing.
 */
const CartHelpStatus: React.FunctionComponent<
  React.PropsWithChildren<{id: string; className?: string}>
> = ({id, className, children}) => {
  const [hasPainted, setHasPainted] = React.useState(false);
  React.useEffect(() => setHasPainted(true), []);
  return (
    <p id={id} className={className} role="status">
      {hasPainted ? children : null}
    </p>
  );
};

const OrderImageryModal: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  feature,
  role,
  selectedProjectId,
  isOpen = true,
  onClose,
  onOrderTypeChange,
  orderableScene,
  getCurrentImageryContract,
  disabledSources = C.DISABLE_ORDERS_SOURCE_IDS,
  blockedSources = C.THUMBNAIL_BLOCKED_SOURCES,
  subsetFeature,
  organization,
  orderableScenes,
  projectName,
  previewScenes = [],
  onPreviewSceneChange,
}) => {
  const [isFeatureHidden, setIsFeatureHidden] = React.useState<boolean>(false);
  const [_organization, profile] = useUserInfo();
  const pushNotification = usePushNotification();
  const {addItem, findBySceneKey, items, isFull, maxItems, setPreviewOpen} = useCart();
  const removeWithUndo = useRemoveWithUndo();

  const {url, sourceDetails} = featureUtils.imageAndSourceDetailsFromScene(orderableScene);
  const sourceId = orderableScene.get('sourceId');
  const sensingTime: string = orderableScene.get('sensingTime');

  // Derive initial order type from the scene's source ID, but allow user to override
  // For non-bundleable sources, default to 'truecolor' (the value doesn't matter as bundle UI won't be shown)
  const derivedOrderType = getOrderTypeFromSourceId(sourceId) ?? 'truecolor';
  const [selectedOrderType, setSelectedOrderType] =
    React.useState<VariableOrderType>(derivedOrderType);

  // Reset selected order type when the scene changes (e.g., switching between truecolor/multispectral)
  React.useEffect(() => {
    setSelectedOrderType(derivedOrderType);
  }, [derivedOrderType]);

  const {prices: imageryPrices} = useImageryPrices();

  const orderBboxString = subsetFeature?.geometry
    ? JSON.stringify(bbox(subsetFeature.geometry))
    : undefined;

  const pricePerAcre: number = imageryPrices[sourceId];

  const thumbnailOverrideScene = React.useMemo(
    () => getThumbnailOverrideScene(orderableScene, orderableScenes),
    [orderableScene, orderableScenes]
  );

  const {url: thumbnailOverrideUrl} = React.useMemo(
    () =>
      thumbnailOverrideScene
        ? featureUtils.imageAndSourceDetailsFromScene(thumbnailOverrideScene)
        : {url: undefined},
    [thumbnailOverrideScene]
  );

  // A scene and its multispectral twin share one slot, so toggling the order type keeps the
  // footer, and Next still moves along the list the modal was opened from.
  const previewIndex = React.useMemo(() => {
    const key = orderableScenePairKey(orderableScene);
    return previewScenes.findIndex((scene) => orderableScenePairKey(scene) === key);
  }, [previewScenes, orderableScene]);

  // modal copy link: stores imageRef, plus partial order geometry (as a bbox) if presesnt. The
  // link rehydrates that bbox as one rectangle, so only a single-rectangle area can ride along.
  const modalUrl = routeUtils.makeHighResSceneUrl({
    organizationId: organization.get('id'),
    projectId: selectedProjectId,
    featureLensId: feature.getIn(['properties', 'lensId']),
    sourceId: orderableScene.get('sourceId'),
    date: orderableScene.get('sensingTime'),
    orderBboxString: isRectanglePolygon(subsetFeature?.geometry) ? orderBboxString : undefined,
  });

  const sceneCoverage = featureUtils.getSceneCoverageOrNull(orderableScene, feature);
  const sceneCoverageFailed = sceneCoverage === null;
  const isPartialScene = (sceneCoverage ?? 0) < 1;

  const {billableAcres, orderGeometryAcres} = getBillableAcres(
    feature,
    orderableScene,
    subsetFeature
  );

  const subsetFeatureAcres = React.useMemo(() => {
    return (
      subsetFeature &&
      Math.floor(conversionUtils.convert(UTMArea(subsetFeature), C.UNIT_AREA_M2, 'areaAcre'))
    );
  }, [subsetFeature]);

  const isDisabledSource = disabledSources.includes(sourceId);

  // An org that pays at checkout bills none of its contracts, so it can order without one, and only
  // its budget can cap it.
  const orgPaysAtCheckout = paysAtCheckout(organization);

  const [contractAfterMs, refreshContractAfterMs] = useTimestampMs();
  const imageryContractStatusMaybe = getCurrentImageryContract({
    afterMs: contractAfterMs,
    // We won't interrupt with a loading spinner if the last contract was
    // fetched within the last 60 seconds, but we will still fetch the latest.
    allowStaleMs: contractAfterMs - 60 * 1000,
  });
  const imageryBudgetQuery = useCurrentImageryBudget(organization.get('id'));
  const purchaseStatus = purchaseInfoStatus(imageryContractStatusMaybe, imageryBudgetQuery);
  const imageryBudget = imageryBudgetQuery.data ?? null;

  // Guarantee that if we have an imagery contract it is of type dollarUnit. AcreUnit
  // contracts are no longer created and non are active. It still exists in the type
  // because we display info about expired contracts in the org settings
  const imageryContract =
    imageryContractStatusMaybe.value &&
    imageryContractStatusMaybe.value.contractType === C.CONTRACT_TYPE_DOLLAR_UNIT
      ? imageryContractStatusMaybe.value
      : null;

  const canBillOrder = orgPaysAtCheckout || imageryContract !== null;

  const orderableSceneValidationResult = validateOrderableScene(orderableScene.toJS());

  const isPartiallyOrdered =
    !orderableScene.get('isFullyOrdered') && orderableScene.get('isOrdered');
  const isOrderingSubset = !!subsetFeature;

  const {layerKey, isPackage, hasPreviewImage} = getScenePreviewKind(orderableScene);
  // Only show preview image for packages or layers that don't have a preview image
  // if we are placing a partial order so the user can see the area they are ordering.
  // Otherwise the preview is unhelpful since we have no image to show
  const showImageryPreview = hasPreviewImage || isOrderingSubset;

  const orderExceedsMaximumOrderAcres =
    sourceDetails.maximum_order_acres !== undefined &&
    billableAcres > sourceDetails.maximum_order_acres;

  // A scene over the spending limit can still go in the cart; the cart's checkout is what the limit
  // blocks.
  const canPurchase =
    !isDisabledSource &&
    !orderableSceneValidationResult.error &&
    !sceneCoverageFailed &&
    pricePerAcre != null;

  const sourceName = sourceDetails.name;
  const sourceOperator = sourceDetails.operator;
  const sourceResolution =
    sourceDetails.resolution != null
      ? parseFloat(sourceDetails.resolution.toFixed(3))
      : sourceDetails.resolution;

  const shouldPresentOrderTypeSwitch =
    featureFlags.MULTISPECTRAL_ORDERS(profile) && isBundleableSource(sourceId) && orderableScenes;

  const mayOrder = userUtils.mayOrderImagery(role);

  const featureId: number = feature.get('id');
  const featureCollectionId: number = feature.getIn(['properties', 'featureCollectionId']);

  const cartItemDraft: CartItemDraft | null =
    pricePerAcre == null
      ? null
      : {
          projectId: selectedProjectId,
          featureCollectionId,
          featureId,
          sourceId,
          sensingTime,
          centsPerAcre: pricePerAcre,
          billableAcres,
          ...(subsetFeature?.geometry ? {geometry: subsetFeature.geometry} : {}),
          snapshot: {
            featureName: featureUtils.formatFeatureName(feature.toJS() as ApiFeature),
            projectName: projectName ?? '',
            sourceDisplayName: [sourceDetails.operator, sourceDetails.name]
              .filter(Boolean)
              .join(' '),
            thumbnailUrl: thumbnailOverrideUrl ?? url ?? null,
            ...(thumbnailOverrideUrl ? {thumbnailIsOverride: true} : {}),
          },
        };

  // The truecolor and multispectral variants of a scene carry different source ids, so they take
  // separate cart slots. Within one slot the sub-area is the only thing that can differ, and it
  // often doesn't: `subsetFeature` comes from the parent's map-draw state, which only carries the
  // stored sub-area when the modal was opened from the item's deep link.
  const itemInCart = mayOrder ? findBySceneKey({featureId, sourceId, sensingTime}) : undefined;

  // Bboxes, not the polygons themselves: two different shapes can share one, so this fails towards
  // saying nothing, never towards claiming an area changed when it didn't.
  const storedAreaDiffers = !!itemInCart && cartItemOrderBboxString(itemInCart) !== orderBboxString;

  // A feature collection can sit in several projects, so the same scene can be reopened under a
  // project other than the one the item was added from.
  const storedProjectDiffers = !!itemInCart && itemInCart.projectId !== selectedProjectId;

  const cartHelpId = React.useId();

  // A refused add leaves the modal open with something to fix, so it stands beside the button
  // rather than expiring as a toast the user may already have looked away from. It describes one
  // click against one cart, so reconfiguring the order or moving the cart retires it. Refusing
  // paths never commit, so the reset below can't eat the message the click just set.
  const [cartRefusal, setCartRefusal] = React.useState<string | null>(null);

  React.useEffect(() => {
    setCartRefusal(null);
  }, [sourceId, sensingTime, orderBboxString, items]);

  const confirmCartChange = (message: string) => {
    pushNotification({
      message,
      autoHideDuration: 5000,
      options: {
        action: {text: 'View cart', onClick: () => setPreviewOpen(true)},
      },
    });
    onClose();
  };

  const showCapReached = () => setCartRefusal(cartFullMessage(maxItems));

  const confirmAdded = () => confirmCartChange('Added to your cart.');

  const handleAddToCart = () => {
    setCartRefusal(null);
    if (!cartItemDraft) {
      return;
    }

    const result = addItem(cartItemDraft);
    if (result === 'added') {
      confirmAdded();
    } else if (result === 'cap-reached') {
      showCapReached();
    } else {
      // The scene reached the cart from elsewhere while this modal was open, so nothing was
      // written. Saying so re-renders the modal with the remove CTA in place of this button.
      setCartRefusal('This scene is already in your cart.');
    }
  };

  // What the cart will and won't do with this scene. A scene that can't be ordered at all is left to
  // the copy above, which explains it better than the cart can.
  const cartHelpText = ((): string | null => {
    // The "In cart" tag and the button's own label already say the scene is in there, with no
    // click needed for either, so what would change is the only part left to explain.
    if (itemInCart) {
      if (storedAreaDiffers) {
        return 'This scene is already in your cart with a different area. Remove it to add this one.';
      }
      if (storedProjectDiffers) {
        return `This scene is already in your cart under ${
          itemInCart.snapshot.projectName || 'another project'
        }. Remove it to add it here.`;
      }
      return null;
    }
    return canPurchase && isFull ? cartFullMessage(maxItems) : null;
  })();

  // A refusal answers the click that just happened, so it stands in for the state text rather than
  // stacking above it — the state text is what the refusal is correcting.
  const cartMessage = cartRefusal ?? cartHelpText;

  // One slot, not two buttons editing the same item: a scene that's in the cart comes out and
  // goes back in, which is the only way to store an area or project other than the stored one.
  const cartButton = itemInCart ? (
    <B.AnchorButton
      large
      icon="trash"
      text="Remove from cart"
      aria-describedby={cartMessage ? cartHelpId : undefined}
      onClick={() => removeWithUndo(itemInCart)}
    />
  ) : (
    <B.AnchorButton
      intent={B.Intent.PRIMARY}
      large
      icon="shopping-cart"
      // The cart's own line price, minimum order included, so the two always agree.
      text={cartItemDraft ? `Add to cart · ${formatItemPrice(cartItemDraft)}` : 'Add to cart'}
      disabled={!canPurchase || !cartItemDraft || isFull}
      aria-describedby={cartMessage ? cartHelpId : undefined}
      onClick={handleAddToCart}
    />
  );

  const cartHelp = mayOrder && (
    <CartHelpStatus
      id={cartHelpId}
      className={classnames(cs.cartHelp, {[cs.cartRefusal]: cartRefusal !== null})}
    >
      {cartMessage}
    </CartHelpStatus>
  );

  let acresMessage = '';
  const subsetAreaCount =
    subsetFeature?.geometry.type === 'MultiPolygon' ? subsetFeature.geometry.coordinates.length : 1;
  const subsetSelectionLabel =
    subsetAreaCount > 1
      ? `Your ${subsetAreaCount} selected areas, outlined in yellow, include`
      : 'Your partial area selection, outlined in yellow, includes';
  if (subsetFeature && subsetFeatureAcres === orderGeometryAcres) {
    // partial order that is fully contained within property
    acresMessage = `${subsetSelectionLabel} ${formatAcres(
      orderGeometryAcres
    )} acres within your property at ${formatCost(pricePerAcre)} per acre.`;
  } else if (subsetFeature) {
    // partial order whose shape extends outside of property lines
    acresMessage = `${subsetSelectionLabel} ${formatAcres(
      orderGeometryAcres
    )} new acres within your property at ${formatCost(pricePerAcre)} per acre.`;
  } else if (isPartiallyOrdered) {
    // ordering the rest of a scene that has been partially ordered
    acresMessage = `Your selection includes the remaining ${formatAcres(
      orderGeometryAcres
    )} acres available in this scene at ${formatCost(pricePerAcre)} per acre.`;
  } else {
    // standard order case
    acresMessage = `Your selection includes ${formatAcres(
      orderGeometryAcres
    )} acres of ${getResolutionCategoryForScene(orderableScene) == 'data' ? 'data' : 'imagery'} at ${formatCost(pricePerAcre)} per acre.`;
  }

  if (orderGeometryAcres === billableAcres) {
    acresMessage += ' You will only be billed for the area in your property boundary.';
  }

  return (
    <B.Overlay isOpen={isOpen} hasBackdrop={true} onClose={onClose}>
      <MapOverlayDialog
        title="Preview"
        onClose={onClose}
        className={classnames(cs.dialog, {[cs.narrowDialog]: !showImageryPreview})}
        contentClassName={cs.dialogContent}
        footer={
          previewIndex >= 0 &&
          previewScenes.length > 1 && (
            <ScenePreviewControls
              className={cs.previewFooter}
              count={previewScenes.length}
              index={previewIndex}
              onIndexChange={(nextIndex) => onPreviewSceneChange?.(previewScenes[nextIndex])}
            />
          )
        }
        additionalButtons={
          <>
            <B.Popover
              content={
                <div style={{padding: '1rem', maxWidth: '250px'}}>
                  See our{' '}
                  <a
                    href="https://lens.tech/support/article/136-choosing-imagery"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    guidance for choosing imagery here
                  </a>
                  , or track imagery spending from the{' '}
                  <Link
                    to={`/${getOrgPrefix(organization)}/settings/billing`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    billing page here
                  </Link>
                  .
                </div>
              }
              position={B.Position.BOTTOM_RIGHT}
              interactionKind={B.PopoverInteractionKind.HOVER}
              usePortal={false}
            >
              <B.AnchorButton icon="info-sign" minimal={true} />
            </B.Popover>
            {!isPackage && (
              <B.Tooltip
                content={isFeatureHidden ? 'Hide boundary in preview' : 'Show boundary in preview'}
                position={B.Position.TOP}
              >
                <B.AnchorButton
                  icon={isFeatureHidden ? 'eye-open' : 'eye-off'}
                  minimal={true}
                  onClick={() => {
                    setIsFeatureHidden(!isFeatureHidden);
                  }}
                />
              </B.Tooltip>
            )}
            <B.Tooltip content="Copy link" position={B.Position.TOP}>
              <CopyToClipboard text={modalUrl}>
                <B.AnchorButton icon="clipboard" minimal={true} />
              </CopyToClipboard>
            </B.Tooltip>
          </>
        }
      >
        <div className={cs.content}>
          {purchaseStatus === 'unknown' && <B.Spinner className={cs.loadingSpinner} />}
          {purchaseStatus === 'error' && (
            <B.NonIdealState
              iconMuted={false}
              className={cs.errorMessage}
              icon="warning-sign"
              description="We weren’t able to load purchase information for this project right now."
              action={
                <B.Button
                  intent="primary"
                  onClick={() => {
                    refreshContractAfterMs();
                    if (imageryBudgetQuery.isError) {
                      void imageryBudgetQuery.refetch();
                    }
                  }}
                >
                  Try Again
                </B.Button>
              }
            ></B.NonIdealState>
          )}

          {purchaseStatus === 'some' &&
            canBillOrder &&
            (billableAcres > 0 ? (
              <>
                <div className={cs.purchaseInfoContainer}>
                  <div className={cs.purchaseInfoDescription}>
                    {isPackage ? (
                      getPackageOrderText(layerKey)
                    ) : (
                      <>
                        {shouldPresentOrderTypeSwitch && (
                          <OrderTypeSwitcher
                            orderableScenes={orderableScenes}
                            orderableScene={orderableScene}
                            selectedOrderType={selectedOrderType}
                            onOrderTypeChange={(type) => {
                              setSelectedOrderType(type);
                              onOrderTypeChange?.(type);
                            }}
                            organization={organization}
                          />
                        )}
                        <SceneDetails scene={orderableScene} />
                      </>
                    )}

                    {sourceId.startsWith('AIRBUS') && sourceId.endsWith('ARCHIVE') && (
                      <>
                        <hr className={cs.separator} />
                        <B.Callout icon={null} intent={B.Intent.DANGER}>
                          <p>
                            <strong>Warning:</strong> Imagery from the Extended Archive may take up
                            to 72 hours to process, and quality can vary. Please exercise caution
                            and carefully review the preview for any potential issues, including
                            cloud cover, discoloration, and geographical inaccuracies.
                          </p>
                        </B.Callout>
                      </>
                    )}
                    <ContractDetailsDescription
                      isDisabledSource={isDisabledSource}
                      isProcessing={
                        orderableSceneValidationResult.error ===
                        ORDERABLE_SCENE_ERRORS.ORDER_STILL_PROCESSING
                      }
                      sceneCoverageFailed={sceneCoverageFailed}
                      canPurchase={canPurchase}
                      layerKey={getLayerKeyForScene(orderableScene)}
                      sceneSensingTime={orderableScene.get('sensingTime')}
                      orderGeometryAcres={orderGeometryAcres}
                      billableAcres={billableAcres}
                      imageryContract={imageryContract}
                      imageryBudget={imageryBudget}
                      acresMessage={acresMessage}
                      orderExceedsMaximumOrderAcres={orderExceedsMaximumOrderAcres}
                      role={role}
                      isPreview
                    />
                  </div>

                  {itemInCart && (
                    <div className={cs.inCartNotice}>
                      <B.Tag minimal icon="shopping-cart">
                        In cart
                      </B.Tag>
                    </div>
                  )}

                  {cartHelp}

                  <div className={cs.purchaseInfoActions}>
                    {mayOrder ? (
                      isPartialScene ? (
                        <B.Tooltip
                          content={
                            <>
                              <strong>Note:</strong> This data does not fully cover the property
                            </>
                          }
                        >
                          {cartButton}
                        </B.Tooltip>
                      ) : subsetFeature ? (
                        <B.Tooltip
                          content={
                            <>
                              <strong>Note:</strong> This is a partial area order
                            </>
                          }
                        >
                          {cartButton}
                        </B.Tooltip>
                      ) : (
                        cartButton
                      )
                    ) : (
                      <CopyToClipboard text={modalUrl}>
                        <B.Button intent={B.Intent.PRIMARY} large text="Copy Link" type="submit" />
                      </CopyToClipboard>
                    )}
                  </div>
                </div>

                {showImageryPreview && (
                  <div className={cs.previewArea}>
                    <div className={cs.previewImageContainer}>
                      <ScenePreviewStage
                        source={{feature, scene: orderableScene, orderableScenes, subsetFeature}}
                        size={PREVIEW_STAGE_SIZE}
                        blockedSources={blockedSources}
                        isFeatureHidden={isFeatureHidden}
                      />
                    </div>
                    <div className={cs.previewDescription}>
                      <div>
                        {sourceOperator} {sourceName}
                        {sourceResolution && (
                          <>
                            {' '}
                            (<span className={cs.sourceResolution}>{sourceResolution}m</span>)
                          </>
                        )}
                      </div>
                      <ScenePreviewGuidance isPartialScene={isPartialScene} />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <B.NonIdealState
                iconMuted={false}
                className={cs.errorMessage}
                icon="warning-sign"
                description={
                  <>
                    <p>
                      We weren’t able to find orderable data. It may be that you have already
                      ordered all the data that covers your property.
                    </p>
                    <p>
                      If parts of your property are available but missing from data you’ve already
                      ordered, contact <strong>lens@upstream.tech</strong> for support.
                    </p>
                  </>
                }
              />
            ))}
        </div>
      </MapOverlayDialog>
    </B.Overlay>
  );
};

export default OrderImageryModal;
